import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { FormField } from "@/components/ui/FormField";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import type { AppKind } from "@/api/appVersions.api";
import { APP_LABEL } from "@/features/appVersions/types";

const schema = z.object({
  version: z.coerce
    .number({ invalid_type_error: "Enter the version number" })
    .int("Whole numbers only")
    .min(1, "Must be at least 1"),
  notes: z.string().min(5, "Add short release notes").max(1000, "Max 1000 characters"),
  forceUpdate: z.boolean(),
});

export type ReleaseFormValues = z.infer<typeof schema>;

export function ReleaseModal({
  open,
  app,
  currentVersion,
  saving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  app: AppKind;
  /** The version now live; a new release has to be higher. Null when the app was never set. */
  currentVersion: number | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (values: ReleaseFormValues) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ReleaseFormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    // Always starts Optional: forcing an update should be a deliberate choice for each release.
    if (open) reset({ version: undefined as unknown as number, notes: "", forceUpdate: false });
  }, [open, reset]);

  const forced = !!watch("forceUpdate");

  function submit(values: ReleaseFormValues) {
    if (currentVersion !== null && values.version <= currentVersion) {
      setError("version", { message: `Must be higher than the current version (${currentVersion})` });
      return;
    }
    onSubmit(values);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Register ${APP_LABEL[app]} release`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button form="release-form" type="submit" loading={saving}>
            Publish release
          </Button>
        </>
      }
    >
      <form id="release-form" className="flex flex-col gap-3" onSubmit={handleSubmit(submit)}>
        <p className="text-xs text-ink-faint">
          Register a build only after it is live in both stores — users would otherwise be prompted to a version they can't
          yet install. It becomes the latest version, so users on older versions are asked to update.
        </p>
        <FormField
          label="Update type"
          hint={
            forced
              ? "Full-screen — users on an older version can't use the app until they update"
              : "Dismissible prompt — users on an older version can choose Later"
          }
        >
          <SegmentedControl
            size="md"
            options={[
              { value: "optional", label: "Optional" },
              { value: "forced", label: "Forced" },
            ]}
            value={forced ? "forced" : "optional"}
            onChange={(v) => setValue("forceUpdate", v === "forced", { shouldDirty: true })}
          />
        </FormField>
        <FormField
          label="Version"
          error={errors.version?.message}
          hint={currentVersion !== null ? `Current version is ${currentVersion}` : undefined}
        >
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            placeholder={currentVersion !== null ? String(currentVersion + 1) : "1"}
            className="font-mono"
            error={!!errors.version}
            {...register("version")}
          />
        </FormField>
        <FormField label="Release notes" error={errors.notes?.message}>
          <Textarea rows={3} {...register("notes")} />
        </FormField>
      </form>
    </Modal>
  );
}
